"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.autoSaveEntityReducer = exports.mergeEntityObjects = exports.mergeEntityArrays = void 0;
const UnsupportedValueError_1 = __importDefault(require("../../util/UnsupportedValueError"));
const lodash_1 = require("lodash");
/**
 * Merge together two arrays of entities, patching existing elements.
 *
 * For any elements in `oldArr` that have `id` keys, these are paired with elements in `newArr` and merged ({...old, ...new}).
 * For elements _without_ id, we use those from newArr, as they are the latest data.
 *
 * @param {T[]} existingArr The array of elements that already exist
 * @param {T[]} newArr The updated array of elements
 *
 * @returns {T[]} outArr - The array with updated data.
 *
 * @example
 * const oldArr = [{id: 1, name: 'Johnny'}, {id: 2, name: 'Jeff'}, {name: 'Jim'}]
 * const newArr = [{id: 1, name: 'John', age: 29}, {id: 2, name: 'Geoff', age: 35}, {name: 'Jim', age: 40}]
 *
 * mergeEntityArrays(oldArr, newArr)
 * > [{id: 1, name: 'John', age: 29}, {id: 2, name: 'Geoff', age: 35}, {name: 'Jim', age: 40}]
 */
const mergeEntityArrays = (oldArr, newArr) => {
    const newWithIds = [];
    const newNoIds = [];
    newArr.forEach((e) => e.id !== undefined ? newWithIds.push(e) : newNoIds.push(e));
    // Filter out entities _without_ ids in oldArr, and patch the ones that do have an ID
    const withIdsPatched = [];
    oldArr.filter((e) => e.id !== undefined).forEach((e) => {
        const newE = newWithIds.find((n) => n.id === e.id);
        // Entity was deleted in newArr, so remove
        if (!newE)
            return;
        // Patch the object, preferring new data
        withIdsPatched.push(Object.assign(Object.assign({}, e), newE));
    });
    return [...withIdsPatched, ...newNoIds];
};
exports.mergeEntityArrays = mergeEntityArrays;
const partialEquals = (existingObj, newObj) => {
    // Make sure everything that is in the existing object is also in the new
    for (const [key, value] of Object.entries(existingObj)) {
        if (!(key in newObj)) {
            return false;
        }
        if (value !== newObj[key]) {
            return false;
        }
    }
    return true;
};
/**
 * Merge together two object entities, patching any existing object properties with data from the new object.
 *
 * We merge the objects together using lodash's recursive merge, unless the property is an array.
 * For array properties, we take all of the existing elements in the array and
 * populate them with additional data from the equivalent element in the new array.
 * This means we do not add new elements from the new array into the existing.
 *
 * This array-specific logic is required due to the way that subdocuments like amenities work
 *
 * @param {T} existingObj The object entity that already exists
 * @param {T} newObj The updated object entity
 *
 * @returns {T} mergedObj - The object with updated data
 *
 * @example
 * const existingObj = 	{id: 123, people: [{id: 1, name: 'Johnny'}, {name: 'Jim'}]}
 * const newArr = 		{id: 123, people: [{id: 1, name: 'Johnny'}, {id: 2, name: 'Jim'}, {id: 3, name: 'Geoff'}], group: 'blue'}
 *
 * mergeEntityObjects(existingObj, newArr)
 * > {id: 123, people: [{id: 1, name: 'Johnny'}, {id: 2, name: 'Jim'}], group: 'blue'}
 */
const mergeEntityObjects = (existingObj, newObj) => {
    if (existingObj === null) {
        return null;
    }
    const merged = (0, lodash_1.mergeWith)(newObj, existingObj, (newProperty, existingProperty) => {
        // If the existing property isn't an array, merge the elements as normal
        if (!Array.isArray(existingProperty)) {
            return undefined;
        }
        // If we have no equivalent new property, just return the existing one
        if (!Array.isArray(newProperty)) {
            return existingProperty;
        }
        if (!newProperty.every(np => typeof np === typeof newProperty[0])) {
            throw new Error('Not all elements in the array belonging to the new object are of the same type.');
        }
        /**
         * Naive approach : check that the array items are all strings booleans, or numbers, if so just return new array.
         * The reason we are doing this here is because we do not need to compare the incoming data in the array as it is
         * impossible for there to be partial data that needs to be merged into an object since numbers, strings and booleans
         * cannot be partial entities.
         */
        const simpleArrayTypes = ['string', 'number', 'boolean'];
        if (existingProperty.every(v => simpleArrayTypes.includes(typeof v)) &&
            newProperty.every(v => simpleArrayTypes.includes(typeof v))) {
            return newProperty;
        }
        const mergedArr = [];
        // We only want items from the existing array, as the new may contain since-removed data
        // But we may have some content that is only in the new one (e.g. the ID), so hydrate the existing with the new data
        for (const existingElement of existingProperty) {
            let finalElement = existingElement;
            // Get the equivalent element in the new property
            for (const newElement of newProperty) {
                // we know the array being iterated over either contains nest array items or objects
                // the below function only covers items of type object, NOT nested arrays
                if (partialEquals(existingElement, newElement)) {
                    // If there's an equivalent item in the new one, merge them to hydrate the existing one
                    finalElement = Object.assign(Object.assign({}, newElement), existingElement);
                    break;
                }
            }
            mergedArr.push(finalElement);
        }
        ;
        return mergedArr;
    });
    return merged;
};
exports.mergeEntityObjects = mergeEntityObjects;
/**
 * A reducer to update an entity (or array of entities).
 *
 * It models our auto-saving behaviour of periodically syncing data to our back-end. In `UPDATE_INPUT` cases, we prefer
 * the new action payload as that is a user making changes live.
 *
 * In `UPDATE_SYNC` cases, we prefer the existing state, as it's possible that the user has made subsequent changes since the
 * last data sync occurred.
 *
 * @param {T | T[] | null} state - The state of the reducer
 * @param {AutoSaveEntityAction<T>} action  - The action to perform on the reducer
 *
 * @returns {T | T[] | null} state - The new state of the reducer
 */
const autoSaveEntityReducer = (state, action) => {
    switch (action.type) {
        case 'LOAD':
            return action.payload;
        case 'UPDATE_INPUT': {
            if (Array.isArray(action.payload)) {
                // action.payload is latest user updates, so prefer those to existing
                // state when patching
                return (0, exports.mergeEntityArrays)((state || []), action.payload);
            }
            else {
                return state === null ? action.payload : Object.assign(Object.assign({}, state), action.payload);
            }
        }
        case 'UPDATE_SYNC': {
            if (Array.isArray(action.payload)) {
                // action.payload is latest data synced to backend, so it _may_ be out of date
                // vs state if user has made changes in the meantime.
                //
                // Prefer existing state to action.payload when patching
                return (0, exports.mergeEntityArrays)(action.payload, (state || []));
            }
            else {
                // We want to make sure we merge the objects to make sure state takes precedence,
                // but that the state is populated with data from the backend (e.g. the entity ID)
                // We need to be careful with array properties, where we don't want to accidentally
                // re-add elements that have not yet been removed from the backend data
                return (0, exports.mergeEntityObjects)(state, action.payload);
            }
        }
        default:
            throw new UnsupportedValueError_1.default(action);
    }
};
exports.autoSaveEntityReducer = autoSaveEntityReducer;
//# sourceMappingURL=reducer.js.map